<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { compareIdentifiers } from "@persolog/utils";
import { computed, onMounted, ref } from "vue";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../api/index.ts";
import { Product, ProductActivation } from "../types/PersologOnlineTypes.ts";
import { FilterMatchMode } from "@primevue/core/api";
import { getDisplayName } from "../main.ts";
import { useConfirm } from "primevue";

const { t } = useI18n();
const confirm = useConfirm();

const loadingAll = ref(false);
const templateStatus = ref({ product: "", status: "" });
const loading = ref({ product: "", language: "" });
const languages = ref<string[]>([]);
const allProducts = ref<Product[]>([]);
const products = ref<ProductActivation[]>([]);
const questionnaires = ref<ProductActivation[]>([]);
const complete = ref<
    {
        name: string;
        productLanguages: string[];
        questionnaireLanguages: string[] | undefined;
    }[]
>([]);

const enriched = ref<
    {
        name: string;
        productLanguages: {
            name: string;
            active: boolean;
        }[];
        questionnaireLanguages: {
            name: string;
            active: boolean | undefined;
        }[];
    }[]
>([]);

const LANGUAGE_OVERVIEW_URL = "https://persolog.online/language-overview";

// Keep skeleton rows stable across renders to avoid DataTable update loops.
const skeletonRows = ref([{}, {}, {}]);
const EMPTY_PIPELINE_ITEMS: Array<{ label: string; command: () => void }> = [];

async function getProductLanguages() {
    const all = await LanguageManagementAPI.getProducts(true);
    products.value = all.filter((product) => !product.name.startsWith("questionnaire-"));
    questionnaires.value = all.filter((product) => product.name.startsWith("questionnaire-"));
    complete.value = products.value.map((product) => {
        const questionnaire = questionnaires.value.find((questionnaire) => questionnaire.name === "questionnaire-" + product.name);
        return {
            name: product.name,
            productLanguages: product.languages,
            questionnaireLanguages: questionnaire?.languages,
        };
    });
    questionnaires.value.forEach((questionnaire) => {
        const product = products.value.find((product) => "questionnaire-" + product.name === questionnaire.name);
        if (!product) {
            complete.value.push({
                name: questionnaire.name.split("-")[1],
                productLanguages: [],
                questionnaireLanguages: questionnaire.languages,
            });
        }
    });
    allProducts.value.forEach((product) => {
        if (!products.value.some((p) => p.name === product.productNo)) {
            complete.value.push({
                name: product.productNo,
                productLanguages: [],
                questionnaireLanguages: [],
            });
        }
    })
    enriched.value = complete.value.map((product) => {
        return {
            name: product.name,
            productLanguages: languages.value.map((language) => {
                const active = product.productLanguages.includes(language);
                return {
                    name: language,
                    active: active,
                };
            }),
            questionnaireLanguages: languages.value.map((language) => {
                const active = product.questionnaireLanguages?.includes(language);
                return {
                    name: language,
                    active: active,
                };
            }),
        };
    });
    enriched.value.sort((a, b) => compareIdentifiers(a.name, b.name));
}

onMounted(async () => {
    loadingAll.value = true;
    languages.value = (await LanguageManagementAPI.getLanguages())
        .filter((language) => !["und", "zh", "cz", "jp"].includes(language))
        .sort((a, b) => a.localeCompare(b));
    allProducts.value = await ProfileAdministrationAPI.getAllProducts();
    await getProductLanguages();
    loadingAll.value = false;
});

const filters = ref({
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
});

async function activate(product: string, language: string, active: boolean) {
    loading.value = { product: product, language: language };
    await LanguageManagementAPI.activateOrDeactivateProduct(product, language, active);
    await getProductLanguages();
    loading.value = { product: "", language: "" };
}

function confirmActivation(product: string, language: string, active: boolean) {
    confirm.require({
        group: "confirm-activation-" + product,
        message: active
            ? t("products.activation.confirm.message.on", {
                  product: product.toUpperCase(),
                  language: language,
              })
            : t("products.activation.confirm.message.off", {
                  product: product.toUpperCase(),
                  language: language,
              }),
        header: t("products.activation.confirm.header"),
        rejectProps: {
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            id: `product-view_confirm_activation-${product}`,
            label: t("buttons.save"),
        },
        accept: () => {
            activate(product, language, active);
        },
    });
}

async function releaseTemplate(product: string) {
    templateStatus.value = { product: product, status: "loading" };
    try {
        await LanguageManagementAPI.releaseTemplate(product);
        templateStatus.value = { product: product, status: "success" };
    } catch (error) {
        templateStatus.value = { product: product, status: "error" };
        console.error("Error releasing template:", error);
    } finally {
        setTimeout(() => {
            templateStatus.value = { product: "", status: "" };
        }, 2000);
    }
}

const templateSeverities = computed(() => {
    const map = new Map<string, string>();
    if (templateStatus.value.product) {
        if (templateStatus.value.status === "success") {
            map.set(templateStatus.value.product, "success");
        } else if (templateStatus.value.status === "error") {
            map.set(templateStatus.value.product, "danger");
        }
    }
    return map;
});

const pipelineItemsByProduct = computed(() => {
    const items = new Map<string, Array<{ label: string; command: () => void }>>();
    for (const product of products.value) {
        items.set(
            product.name,
            product.languages.map((language) => ({
                label: getDisplayName(language) || language,
                command: () => {
                    openPipeline(product.name, language);
                },
            })),
        );
    }
    return items;
});

function openPipeline(product: string, language: string) {
    window.open(
        'https://gitlab.persolog.com/persolog-online/testing/-/pipelines/new' +
            `?var[PROFILE]=${product.toUpperCase()}&var[LOCALE]=${language}&var[STAGE]=staging&var[REF]=true`,
        "_blank",
    );
}
</script>

<template>
    <div class="language-activation-page">
        <h2 class="page-title mt-6 mb-1">{{ t("product.activation.heading.products") }}</h2>
        <p class="text-content-muted mt-0 mb-1" data-cy="language-activation-intro">{{ t("products.activation.intro") }}</p>
        <p class="text-content-muted mt-0 mb-6">
            {{ t("products.activation.overview") }}
            <a :href="LANGUAGE_OVERVIEW_URL" target="_blank" rel="noopener noreferrer" data-cy="language-activation-overview">persolog.online/language-overview</a>
        </p>
        <DataTable v-if="loadingAll" :value="skeletonRows" tableStyle="min-width: 50rem">
        <Column field="name" :header="t('products.activation.table.name')">
            <template #body>
                <Skeleton></Skeleton>
            </template>
        </Column>
        <Column field="productLanguages" :header="t('products.activation.table.product')">
            <template #body>
                <Skeleton></Skeleton>
            </template>
        </Column>
        <Column field="questionnaireLanguages" :header="t('products.activation.table.questionnaire')">
            <template #body>
                <Skeleton></Skeleton>
            </template>
        </Column>
        <Column field="test" :header="t('products.activation.table.pipeline.header')">
            <template #body>
                <Skeleton></Skeleton>
            </template>
        </Column>
    </DataTable>
    <DataTable
        v-else
        id="product-view_table_product-activation"
        v-model:filters="filters"
        filter-display="row"
        :value="enriched"
        tableStyle="min-width: 50rem"
        paginator
        :rows="10"
    >
        <Column field="name" :header="t('products.activation.table.name')">
            <template #body="{ data }">
                <span class="font-bold text-xl">{{ data.name?.toUpperCase() }}</span>
                <Button
                    class="mr-1"
                    text
                    :id="'product-view_template_' + data.name"
                    size="small"
                    :severity="templateSeverities.get(data.name) || 'secondary'"
                    @click="releaseTemplate(data.name)"
                    v-tooltip.top="{
                        value: t('products.activation.table.release', { product: data.name?.toUpperCase() }),
                        showDelay: 500,
                    }"
                >
                    <template #icon>
                        <span
                            :class="[ 'custom-fly-wrapper', { 'fall-down': templateStatus.product === data.name && templateStatus.status === 'error', rotated: templateStatus.product === data.name && templateStatus.status === 'success', flying: templateStatus.product === data.name && templateStatus.status === 'loading', }, ]"
                        >
                            <i class="pi pi-send"></i>
                        </span>
                    </template>
                </Button>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText class="max-w-40" v-model="filterModel.value" type="text" @input="filterCallback()" :placeholder="t('buttons.find')" />
            </template>
        </Column>
        <Column field="productLanguages" :header="t('products.activation.table.product')">
            <template #body="{ data }">
                <Button
                    :id="'product-languages_' + data.name + '_' + language.name"
                    v-tooltip.top="{
                        value: getDisplayName(language.name),
                        showDelay: 500,
                    }"
                    @click="confirmActivation(data.name, language.name, !language.active)"
                    class="m-1 px-2 py-0 text-sm leading-tight"
                    :severity="language.active ? '' : 'secondary'"
                    v-for="language in data.productLanguages"
                    :key="language.name"
                    :label="loading.product === data.name && loading.language === language.name ? '' : language.name"
                    :icon="loading.product === data.name && loading.language === language.name ? 'pi pi-spinner pi-spin' : ''"
                />
                <ConfirmDialog :group="'confirm-activation-' + data.name" />
            </template>
        </Column>
        <Column field="questionnaireLanguages" :header="t('products.activation.table.questionnaire')">
            <template #body="{ data }">
                <Button
                    :id="'questionnaire-languages_' + data.name + '_' + language.name"
                    v-tooltip.top="{
                        value: getDisplayName(language.name),
                        showDelay: 500,
                    }"
                    @click="confirmActivation('questionnaire-' + data.name, language.name, !language.active)"
                    class="m-1 px-2 py-0 text-sm leading-tight"
                    :severity="language.active ? '' : 'secondary'"
                    v-for="language in data.questionnaireLanguages"
                    :key="language.name"
                    :label="loading.product === 'questionnaire-' + data.name && loading.language === language.name ? '' : language.name"
                    :icon="loading.product === 'questionnaire-' + data.name && loading.language === language.name ? 'pi pi-spinner pi-spin' : ''"
                />
                <ConfirmDialog :group="'confirm-activation-questionnaire-' + data.name" />
            </template>
        </Column>
        <Column :header="t('products.activation.table.pipeline.header')">
            <template #body="{ data }">
                <SplitButton
                    :id="'product-view_pipeline_' + data.name"
                    text
                    size="small"
                    icon="pi pi-play"
                    @click="openPipeline(data.name, 'de')"
                    :model="pipelineItemsByProduct.get(data.name) || EMPTY_PIPELINE_ITEMS"
                    v-tooltip.top="{
                        value: t('products.activation.table.pipeline.start', { product: data.name?.toUpperCase() }),
                        showDelay: 500,
                    }"
                />
            </template>
        </Column>
    </DataTable>
    </div>
</template>

<style scoped>
.language-activation-page {
    padding: 1rem;
}

.fall-down {
    transform: rotate(45deg);
    animation: rotate-upside-down 2s linear;
}

.rotated {
    transform: rotate(45deg);
    animation: rotate-back 2s linear;
}

.flying {
    display: inline-block;
    animation: fly-horizontal-loop 2s ease-in-out infinite;
}

@keyframes rotate-back {
    0% {
        transform: rotate(45deg);
        opacity: 1;
    }
    100% {
        transform: rotate(0deg);
        opacity: 1;
    }
}

@keyframes rotate-upside-down {
    0% {
        transform: rotate(45deg);
        opacity: 1;
    }
    80% {
        transform: rotate(135deg);
        opacity: 1;
    }
    100% {
        transform: rotate(135deg);
        opacity: 1;
    }
}

@keyframes fly-horizontal-loop {
    0% {
        transform: rotate(45deg) translateX(0);
        opacity: 1;
    }
    40% {
        transform: rotate(0deg) translateX(60px);
        opacity: 0;
    }
    50% {
        transform: rotate(0deg) translateX(-60px);
        opacity: 0;
    }
    60% {
        transform: rotate(45deg) translateX(0);
        opacity: 1;
    }
    100% {
        transform: rotate(45deg) translateX(0);
        opacity: 1;
    }
}
</style>
